import { IProduct, IProductSchemaUI } from '@monetize/types/app';
import { FilterType } from '@monetize/types/common';
import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useAtom } from 'jotai';
import { useGetListData } from '../../../api/queryUtils';
import {
  MHStack,
  MPageContainer,
  MPageSearchInput,
} from '../../../components/Monetize';
import { useTableColumns } from '../../../components/Monetize/DataTableNew/columnCatalog/useTableColumns';
import { DataTable } from '../../../components/Monetize/DataTableNew/DataTable';
import { ACTION_COLUMN_KEY } from '../../../components/Monetize/DataTableNew/dataTableNewRenderers';
import { DataTableRef } from '../../../components/Monetize/DataTableNew/DataTableTypes';
import { ExportEntityButton } from '../../../components/Monetize/ExportEntityButton';
import { RefreshTableButton } from '../../../components/Monetize/RefreshTableButton';
import { ROUTES } from '../../../constants';
import { useSettingsReadOnlyRule } from '../../../hooks/useSettingsReadOnlyRule';
import {
  tableFilterSelector,
  tablePagerSelector,
  tableSearchTermSelector,
} from '../../../store/global.store';
import {
  FilterStateKeys,
  getDefaultFilter,
  getDefaultPager,
} from '../../../store/store.types';
import {
  buildFilterParamsRequestObject,
  getFiltersApplied,
  getIsTrulyEmptyList,
  transformTableFilterValue,
} from '../../../utils';
import { navigateOrOpenNewWindow } from '../../../utils/misc';
import PageHeader from '../components/PageHeader';
import TableHeader from '../components/TableHeader';
import { ProductActions } from './ProductActions';
import {
  PRODUCT_LIST_CATALOG,
  ProductListColumnContext,
} from './productList.columns';

const ProductListPage: FC = () => {
  const navigate = useNavigate();
  const [initialFilters, persistTableFilter] = useAtom(
    tableFilterSelector(FilterStateKeys.PRODUCT_LIST),
  );
  const tableRef = useRef<DataTableRef>(null);

  const [pager, setPager] = useAtom(
    tablePagerSelector(FilterStateKeys.PRODUCT_LIST),
  );

  const [searchTerm, setSearchTerm] = useAtom(
    tableSearchTermSelector(FilterStateKeys.PRODUCT_LIST),
  );

  const [filters, setFilters] = useState<FilterType[]>(initialFilters);
  const [searchKey] = useState<string>('name');

  const filterParams = useMemo(
    () =>
      buildFilterParamsRequestObject(
        transformTableFilterValue(filters),
        searchTerm,
        searchKey,
      ),
    [filters, searchTerm, searchKey],
  );

  useEffect(() => {
    persistTableFilter(filters);
  }, [filters, persistTableFilter]);

  const {
    data: productList,
    isLoading,
    isFetched,
    isRefetching,
    refetch,
  } = useGetListData<IProduct>('productCatalogProducts', {
    config: pager,
    filters: filterParams,
  });

  const loading = isLoading || (isRefetching && !isFetched);

  const isTrulyEmptyList = getIsTrulyEmptyList({
    loading,
    totalElements: productList?.totalElements || 0,
    filters,
    searchTerm,
    page: pager.page,
  });

  const onResetFilter = () => {
    setFilters(getDefaultFilter(FilterStateKeys.PRODUCT_LIST));
    setPager(getDefaultPager(FilterStateKeys.PRODUCT_LIST));
    setSearchTerm('');
    tableRef.current?.resetFilters();
  };

  const onSelectEdit = useCallback(
    (id: string, openInNewWindow = false) => {
      navigateOrOpenNewWindow(
        navigate,
        ROUTES.getProductEditRoute(id),
        openInNewWindow,
      );
    },
    [navigate],
  );
  const { canCreateSettings } = useSettingsReadOnlyRule();

  const actionBodyTemplate = useCallback(
    (rowData: IProductSchemaUI) => {
      return (
        <ProductActions
          product={rowData}
          source="LIST"
          onSelectEdit={onSelectEdit}
        />
      );
    },
    [onSelectEdit],
  );

  const hasFiltersOrSearchApplied =
    getFiltersApplied(filters) > 0 || !!searchTerm;

  const searchComponentReset = React.useRef<any>(null);

  const columnContext = useMemo<ProductListColumnContext>(
    () => ({ actionBodyTemplate }),
    [actionBodyTemplate],
  );
  const { columns, state: columnState } = useTableColumns(
    PRODUCT_LIST_CATALOG,
    columnContext,
  );

  return (
    <MPageContainer enableAccountSearch>
      <PageHeader />

      <TableHeader
        model="Product"
        showCreateNewBtn={!isTrulyEmptyList && canCreateSettings}
      >
        {!isTrulyEmptyList && (
          <MHStack gap="2">
            <MPageSearchInput
              placeholderKey="Name"
              value={searchTerm}
              onChange={setSearchTerm}
              count={productList?.totalElements}
              resetSearch={searchComponentReset}
              mode="alwaysExpanded"
            />

            <RefreshTableButton refetch={refetch} />
            <ExportEntityButton
              entity="products"
              filters={filters}
              searchKey={searchKey}
              searchTerm={searchTerm}
              sortField={pager.sortField}
              sortOrder={pager.sortOrder}
            />
          </MHStack>
        )}
      </TableHeader>

      <DataTable
        ref={tableRef}
        columns={columns}
        state={columnState}
        data={productList?.content ?? []}
        enablePagination
        pinnedRightColumns={[ACTION_COLUMN_KEY]}
        onRowClick={(data, _tableRow, openInNewWindow) =>
          onSelectEdit(data?.id, openInNewWindow)
        }
        totalElements={productList?.totalElements}
        totalPages={productList?.totalPages}
        pager={pager}
        setPager={setPager}
        initialFilters={filters}
        onFiltersChange={(newFilters) => {
          setFilters(newFilters);
        }}
        isLoading={isLoading || (isRefetching && !isFetched)}
        emptyProps={{
          mainMessage: 'Looks like there are no products here.',
          smallMessage: null,
          btnLabel: 'New Product',
          to: ROUTES.PRODUCT_NEW,
          secondaryBtnLabel: hasFiltersOrSearchApplied
            ? 'Clear filters'
            : undefined,
        }}
        emptyFilterResultsProps={{
          btnLabel: 'Reset Filters',
        }}
        resetFilter={onResetFilter}
      />
    </MPageContainer>
  );
};

export default ProductListPage;
